<template>
    <div class="product-item-container">
        <div class="product-list-title">
            {{ l('SEARCH_MODAL.PRODUCT_SEARCH_RESULTS_TEXT') }} ({{ productsList.length }})
        </div>
        <div v-for="product in productsList"
             :key="product.ID"
             class="product-item mt-1">
            <div class="product-item-left">
                <div class="product-item__name">
                    {{ product.item.name }}
                </div>
                <div class="product-item__cost">
                    {{ currencyFilter(product.item.price) }}
                </div>
            </div>
            <div class="product-item-right">
                <span class="label label-lg mr-1"
                      :class="{
                          'label-neutral': product.suppressedItem,
                          'label-danger text-white': !product.suppressedItem && product.availableQty <= 0,
                          'label-success text-white': !product.suppressedItem && product.availableQty > 0
                      }">

                    <template v-if="product.suppressedItem">
                        {{ l('SEARCH_MODAL.NOT_TRACKED') }}
                    </template>

                    <template v-else>
                        {{ formatProductQuantity(product.availableQty) }}
                        {{ l('SEARCH_MODAL.PRODUCT_SEARCH_QTY_TITLE') }}
                    </template>
                </span>
                <SquareBtn color="primary"
                           :filled="true"
                           :data-testid="`product-${product.iD}-add-cart-btn`"
                           :data-analytics-id="`Search / Products / Add to Cart ${product.iD}`"
                           icon="fa-solid fa-cart-shopping"
                           class="search-cart"
                           @click="onClick(product)">
                    <span class="fa-solid fa-cart-shopping"></span>
                </SquareBtn>
            </div>
        </div>
    </div>
</template>

<script>
import CartLine from '@/lib/Cart/CartLine';
import SquareBtn from '@/vue/components/SquareBtn';
import { container } from '@/container';
import { generateStockLevelMessage } from '@/libraries/DigiTickets/StockLevelHelper';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';

export default {
    name: 'ProductItem',

    components: {
        SquareBtn,
    },

    props: {
        productsList: {
            type: Array,
            required: true,
            default: () => [],
        },
    },

    emits: [
        'itemClicked',
    ],

    methods: {

        async onClick(product) {
            let stockConfirmed = false;

            const cartItem = new CartLine(
                container.cartService,
                product.item,
                container.userService.currentItemBranch
            );

            const {
                apiStockControlFoodAndDrinkProducts = false,
                apiStockControlProductsExcludingFoodAndDrink = false,
            } = container.userService.company || {};
            const { isFood } = product.item;
            /**
             * @type {boolean}
             */
            const isStockControlAllowed = (
                (isFood && apiStockControlFoodAndDrinkProducts)
                || (!isFood && apiStockControlProductsExcludingFoodAndDrink)
            );

            const isNoStock = product.availableQty <= 0;
            if (isStockControlAllowed && isNoStock) {
                stockConfirmed = await this.showStockLevelModal(container.cartService.currentCart, cartItem);
                if (!stockConfirmed) {
                    return;
                }
            }

            const setDisableStockValidation = stockConfirmed && isNoStock;

            this.$emit('itemClicked', cartItem, setDisableStockValidation);
        },

        /**
         *
         * @param {Cart} cart
         * @param {CartLine} cartItem
         *
         * @return {Promise<boolean>}
         */
        async showStockLevelModal(cart, cartItem) {
            const message = generateStockLevelMessage(cart, cartItem, true);
            const confirmed = await container.dialogService.confirm('', {
                customHtml: message,
                title: 'ORDER_DETAILS.STOCK_LEVEL_MODAL_TITLE',
                cancelLabel: 'ORDER_DETAILS.STOCK_LEVEL_REJECT_PURCHASE',
                okLabel: 'ORDER_DETAILS.STOCK_LEVEL_AUTHORISE_PURCHASE',
            });

            return confirmed;
        },

        /**
         *
         * @param {CartLine} product
         *
         * @return {Promise<void>}
         */
        async addItemToCart(product) {
            const newCart = container.cartService.createEmptyCart();
            const cartSet = await container.cartService.setNewCart(newCart, true);
            if (cartSet) {
                await container.cartService.modifyCurrentCart((currentCart) => {
                    currentCart.sellState.autoAddProductItemID = product.item.ID;
                });
                container.navigationService.viewSell();
                container.vueModalFactory.closeAllOfType(MODAL_NAMES__EPOS.SEARCH);
            }
        },

        /**
         *
         * @param {?number} availableQuantity
         *
         * @return {number}
         */
        formatProductQuantity(availableQuantity = 0) {
            return Number.isFinite(availableQuantity) ? Math.max(0, availableQuantity) : 0;
        },
    },
};
</script>

<style lang="less">
@import "../../../../less/variables";

.product-item-container {
    display: flex;
    flex-direction: column;

    .product-list-title {
        font-size: 20px;
    }
}

.product-item {
    display: flex;
    padding: 10px;
    background: white;
    font-size: @font-size-medium;
    font-weight: normal;
    border: 1px solid @btn-default-border;
    color: @dark-blue;
    box-shadow: 0px 0px 8px -6px black;
    border-radius: 8px;
    text-decoration: none;
    text-align: center;
    overflow: hidden;
    justify-content: center;
    align-items: center;

    &--disabled {
        cursor: default;
        opacity: .65;
        color: @btn-disabled-color !important;
        background: @btn-disabled-bg !important;
        border-color: @btn-disabled-border !important;
    }

    .product-item__name {
        color: @dark-blue;
        font-size: @font-size-large;
        overflow-wrap: break-word;
        word-break: break-word;
    }

    .product-item__cost {
        color: @dt-grey-neutral-700;
    }

    .product-item-left {
        text-align: left;
    }

    .product-item-right {
        display: flex;
        justify-content: right;
        align-items: center;
    }

    .no-stock-icon {
        color: @dt-light-grey;
    }

    .search-cart {
        width: 52px;
        height: 52px;
        font-size: 16px;
    }

    > div {
        flex-grow: 1;
        flex-shrink: 1;
    }
}
</style>
